import { useCallback, useEffect, useRef, useState } from 'react';
import {
  playReviewKey,
  type AcceptedPlayReviewRequest,
} from './use-accepted-play-review.hook';
import type { AgentContext } from '@/hooks/use-agent-chat.hook';
import { useAppDispatch } from '@/store';
import { setAiChatSessionState } from '@/store/features/ui.feature';

export type AgentChatAttachment = {
  agentId: string;
  chatId?: string;
  agentName?: string;
};

export type AgentPlayReviewInterruption = {
  organizationId: string;
  agentId: string;
  playId: string;
};

type Host = {
  key: string;
  agentId: string;
  conversationId: string | null;
  context: AgentContext;
  reviews: readonly AcceptedPlayReviewRequest[];
};

type Attachment = {
  lease: object;
  agentId: string;
  key: string;
  /** The URL that selected this view, independent of a newly started chat. */
  requestedChatId: string | undefined;
};

/** Mounted by the org/user-scoped app shell, never by an Agent page. Leaving
 * a page releases its view lease, not the runtime or its in-flight request. */
export function useAgentChatHosts(enabled: boolean, organizationId?: string) {
  const dispatch = useAppDispatch();
  const [hosts, setHosts] = useState<Host[]>([]);
  const [interruptedPlays, setInterruptedPlays] = useState<
    AgentPlayReviewInterruption[]
  >([]);
  const hostsRef = useRef(hosts);
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const attachmentRef = useRef(attachment);
  const remembered = useRef(new Map<string, string>());
  const accepted = useRef(new Set<string>());
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const commitAttachment = useCallback((next: Attachment | null) => {
    attachmentRef.current = next;
    setAttachment(next);
  }, []);
  const create = useCallback(
    (input: AgentChatAttachment): Host => {
      const key = `agent:${input.agentId}:${crypto.randomUUID()}`;
      if (input.chatId) {
        dispatch(
          setAiChatSessionState({
            key,
            chatState: {
              messages: [],
              conversationId: input.chatId,
              awaitingConfirmation: false,
              pendingConfirmation: null,
            },
          })
        );
      }
      const host: Host = {
        key,
        agentId: input.agentId,
        conversationId: input.chatId ?? null,
        reviews: [],
        context: {
          type: 'agent',
          agent_id: input.agentId,
          agent_name: input.agentName,
        },
      };
      hostsRef.current = [...hostsRef.current, host];
      setHosts(hostsRef.current);
      return host;
    },
    [dispatch]
  );

  const markPlayReviewInterrupted = useCallback(
    (input: AgentPlayReviewInterruption, interrupted: boolean) => {
      if (
        !mounted.current ||
        !enabledRef.current ||
        input.organizationId !== organizationId
      )
        return;
      setInterruptedPlays((rows) => {
        const same = (row: AgentPlayReviewInterruption) =>
          row.agentId === input.agentId && row.playId === input.playId;
        if (interrupted) return rows.some(same) ? rows : [...rows, input];
        return rows.some(same) ? rows.filter((row) => !same(row)) : rows;
      });
    },
    [organizationId]
  );
  useEffect(() => {
    if (!enabled) setInterruptedPlays([]);
  }, [enabled]);

  // Accepted proposals retain their original host without selecting it. The
  // enclosing provider is keyed by org/user, so old callbacks lose authority.
  const enqueueAcceptedReview = useCallback(
    (request: AcceptedPlayReviewRequest) => {
      if (
        !mounted.current ||
        !enabledRef.current ||
        request.organizationId !== organizationId
      )
        return false;
      if (
        request.kind === 'saved' &&
        hostsRef.current.some((host) =>
          host.reviews.some(
            (row) =>
              row.kind === 'saved' &&
              row.agentId === request.agentId &&
              row.playId === request.playId &&
              row.version > request.version
          )
        )
      )
        return false;
      const identity = JSON.stringify([
        request.agentId,
        request.conversationId,
        playReviewKey(request),
      ]);
      if (accepted.current.has(identity) && request.kind !== 'saved')
        return true;
      accepted.current.add(identity);
      const host =
        hostsRef.current.find(
          (candidate) =>
            candidate.agentId === request.agentId &&
            candidate.conversationId === request.conversationId
        ) ??
        create({ agentId: request.agentId, chatId: request.conversationId });
      hostsRef.current = hostsRef.current.map((candidate) => {
        // A newer saved Play applies across this actor's Agent chats. Keep the
        // old transcript, but do not later launch its now-obsolete queued check.
        const reviews = candidate.reviews.map((item) =>
          request.kind === 'saved' &&
          item.agentId === request.agentId &&
          item.playId === request.playId &&
          playReviewKey(item) !== playReviewKey(request)
            ? { ...item, superseded: true }
            : item
        );
        return candidate.key === host.key
          ? {
              ...candidate,
              reviews: reviews.some(
                (item) => playReviewKey(item) === playReviewKey(request)
              )
                ? reviews.map((item) =>
                    playReviewKey(item) === playReviewKey(request)
                      ? { ...request, superseded: item.superseded }
                      : item
                  )
                : [...reviews, request],
            }
          : { ...candidate, reviews };
      });
      setHosts(hostsRef.current);
      return true;
    },
    [create, organizationId]
  );
  useEffect(() => {
    if (enabled || !hostsRef.current.some((host) => host.reviews.length))
      return;
    hostsRef.current = hostsRef.current.map((host) => ({
      ...host,
      reviews: [],
    }));
    setHosts(hostsRef.current);
  }, [enabled]);

  const attach = useCallback(
    (input: AgentChatAttachment) => {
      if (!enabledRef.current) return () => {};
      const existing = hostsRef.current.find(
        (host) =>
          host.agentId === input.agentId &&
          (input.chatId
            ? host.conversationId === input.chatId
            : host.key === remembered.current.get(input.agentId))
      );
      const host = existing ?? create(input);
      const lease = {};
      remembered.current.set(input.agentId, host.key);
      commitAttachment({
        lease,
        agentId: input.agentId,
        key: host.key,
        requestedChatId: input.chatId,
      });
      return () => {
        // A stale page cleanup must not detach a newer page's selected host.
        if (attachmentRef.current?.lease === lease) commitAttachment(null);
      };
    },
    [commitAttachment, create]
  );

  const reportConversation = useCallback(
    (key: string, conversationId: string | null) => {
      const host = hostsRef.current.find((candidate) => candidate.key === key);
      if (!mounted.current || !host || host.conversationId === conversationId)
        return;
      hostsRef.current = hostsRef.current.map((candidate) =>
        candidate.key === key ? { ...candidate, conversationId } : candidate
      );
      setHosts(hostsRef.current);
    },
    []
  );

  const newChat = useCallback(() => {
    const active = attachmentRef.current;
    if (!mounted.current || !enabledRef.current || !active) return false;
    const previous = hostsRef.current.find((host) => host.key === active.key);
    const host = create({
      agentId: active.agentId,
      agentName: previous?.context.agent_name,
    });
    remembered.current.set(active.agentId, host.key);
    commitAttachment({ ...active, key: host.key });
    return true;
  }, [commitAttachment, create]);

  const openConversation = useCallback(
    (conversationId: string) => {
      const active = attachmentRef.current;
      if (!mounted.current || !enabledRef.current || !active) return;
      const previous = hostsRef.current.find((host) => host.key === active.key);
      const host =
        hostsRef.current.find(
          (candidate) =>
            candidate.agentId === active.agentId &&
            candidate.conversationId === conversationId
        ) ??
        create({
          agentId: active.agentId,
          chatId: conversationId,
          agentName: previous?.context.agent_name,
        });
      remembered.current.set(active.agentId, host.key);
      commitAttachment({ ...active, key: host.key });
    },
    [commitAttachment, create]
  );

  // Deletion is explicit. Otherwise retain even idle hosts for this scope:
  // an unsent composer or held queue is not part of the server history.
  const forget = useCallback(
    (keys: readonly string[]) => {
      if (!mounted.current) return;
      const removed = new Set(keys);
      const active = attachmentRef.current;
      if (active && removed.has(active.key)) newChat();
      hostsRef.current = hostsRef.current.filter(
        (host) => !removed.has(host.key)
      );
      setHosts(hostsRef.current);
      for (const [agentId, key] of remembered.current) {
        if (removed.has(key)) remembered.current.delete(agentId);
      }
      for (const key of removed) dispatch(setAiChatSessionState({ key }));
    },
    [dispatch, newChat]
  );

  return {
    hosts: enabled ? hosts : [],
    attachment: enabled ? attachment : null,
    attach,
    enqueueAcceptedReview,
    interruptedPlays,
    markPlayReviewInterrupted,
    newChat,
    openConversation,
    reportConversation,
    forget,
  };
}
